/** Màn «Nhật ký công việc»: gom theo ngày, đếm, và tìm việc ĐẾN GIỜ — hàm thuần, có test. */
import { dateOrdinal, type CalendarDate } from './dates';
import {
  compareWithinDay,
  isOnDate,
  minutesLeft,
  type Moment,
  type WorklogEntry,
} from './records/worklog';

export interface WorklogDay {
  date: CalendarDate;
  entries: WorklogEntry[];
  doneCount: number;
}

export interface WorklogOverview {
  today: WorklogDay;
  /** Các ngày TRƯỚC hôm nay có việc, mới nhất trước. */
  pastDays: WorklogDay[];
  /** Tổng số dòng nhật ký trong phần đang xem (kể cả việc không đọc được ngày). */
  totalEntries: number;
}

function toDay(date: CalendarDate, entries: WorklogEntry[]): WorklogDay {
  const sorted = [...entries].sort(compareWithinDay);
  return { date, entries: sorted, doneCount: sorted.filter((e) => e.status === 'done').length };
}

/**
 * Các con số đếm LUÔN tính trên dữ liệu CHƯA lọc «chưa xong» — lọc chỉ đổi cái đang hiện, không
 * đổi sự thật «hôm nay có bao nhiêu việc».
 */
export function groupWorklog(entries: readonly WorklogEntry[], now: Moment): WorklogOverview {
  const todayOrdinal = dateOrdinal(now.date);
  const byDay = new Map<number, { date: CalendarDate; entries: WorklogEntry[] }>();
  for (const entry of entries) {
    if (!entry.date) continue;
    const ordinal = dateOrdinal(entry.date);
    if (ordinal >= todayOrdinal) continue; // hôm nay gom riêng; việc «tương lai» không có ở màn này
    const day = byDay.get(ordinal) ?? { date: entry.date, entries: [] };
    day.entries.push(entry);
    byDay.set(ordinal, day);
  }
  return {
    today: toDay(
      now.date,
      entries.filter((entry) => isOnDate(entry, now.date)),
    ),
    pastDays: [...byDay.entries()]
      .sort(([a], [b]) => b - a)
      .map(([, day]) => toDay(day.date, day.entries)),
    totalEntries: entries.length,
  };
}

export function onlyUnfinished(day: WorklogDay): WorklogDay {
  return { ...day, entries: day.entries.filter((entry) => entry.status === 'todo') };
}

/** Việc của HÔM NAY đã tới/quá giờ nhắc mà chưa xong — cho thanh «ĐẾN GIỜ» và thông báo. */
export function dueTasks(entries: readonly WorklogEntry[], now: Moment): WorklogEntry[] {
  return entries
    .filter((entry) => isOnDate(entry, now.date))
    .filter((entry) => {
      const left = minutesLeft(entry, now);
      return left !== null && left <= 0;
    })
    .sort(compareWithinDay);
}

/** Số trên huy hiệu menu: việc hôm nay chưa xong. */
export function countUnfinishedToday(entries: readonly WorklogEntry[], now: Moment): number {
  return entries.filter((entry) => entry.status === 'todo' && isOnDate(entry, now.date)).length;
}
